// Deterministic seeded PRNG. Neither tanks nor zombies ships one, so the whole
// map generator draws exclusively from here — same (seed, level) => byte-identical map.

/** mulberry32: fast, well-distributed 32-bit PRNG. Returns a function giving [0,1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash a string to a 32-bit int (xmur3 core) — used to fold a seed word + level into one seed. */
export function hashSeed(str: string): number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

/** A small stateful RNG helper with the draws the generator needs. */
export class Rng {
  private next: () => number;
  constructor(seed: number) {
    this.next = mulberry32(seed >>> 0);
  }
  /** float in [0,1). */
  float(): number {
    return this.next();
  }
  /** float in [min,max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
  /** integer in [min,max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }
  /** true with probability p. */
  chance(p: number): boolean {
    return this.next() < p;
  }
  /** uniform pick from an array. */
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  /** weighted pick: weights parallel to items; returns index. */
  weightedIndex(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) total += w;
    let r = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= weights[i];
      if (r < 0) return i;
    }
    return weights.length - 1;
  }
}

/** Fold a seed word and a level index into a single deterministic 32-bit seed. */
export function levelSeed(seedWord: string, level: number): number {
  return (hashSeed(seedWord) ^ Math.imul(level, 2654435761)) >>> 0;
}
